import React, { useState, useEffect, useCallback } from 'react';
import P, { InferProps } from 'prop-types';
import Typography from '@mui/material/Typography';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { SxStyle } from '../../../types';
import FormHelperText from '@mui/material/FormHelperText';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import firebaseApp from '../../../firebase-app';
import { useStringsContext } from '../../../contexts/strings';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import ImageDropzoneDialog from '../image-dropzone-dialog';

const styles: SxStyle = {
  card: {
    display: 'flex',
    alignItems: 'center',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  image: {
    width: '100px',
    height: '100px',
    boxShadow: '0 2px 6px 0 rgba(0,0,0,0.24)',
    borderRadius: '1rem',
    marginRight: '16px',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
  },
};

function ImageField({
  path,
  field,
  setChange,
  isReadOnly,
}: InferProps<typeof ImageField.propTypes>) {
  const { strings } = useStringsContext();
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageAlt, setImageAlt] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    setImageUrl(field.value);
  }, [field]);

  const onFileSelect = useCallback(
    async (file: any) => {
      const fileName = file?.name;
      const fileNameResized = `${fileName.split('.')[0]}_200x200.png`;
      const storageRef = ref(firebaseApp.storage, `pushNotifications/${path}/${fileNameResized}`);

      try {
        await uploadBytes(storageRef, file);
        setTimeout(async () => {
          const url = await getDownloadURL(storageRef);

          setImageUrl(url);
          setImageAlt(fileName);
          if (setChange) {
            setChange(field.key, url, field.validation, field.isTeamDataValue);
          }
        }, 5000);
      } catch (error) {
        setErrorMessage(strings.messages.uploadErrorImage);
      }
    },
    [field, path, setChange, strings]
  );

  return (
    <>
      <Box sx={styles.card}>
        <Avatar
          variant="square"
          src={imageUrl || field.value}
          alt={imageAlt || field.title}
          sx={styles.image}
        />
        <Box sx={styles.actions}>
          <Box sx={styles.column}>
            <Typography variant="subtitle2">{field.title}</Typography>
            <Typography variant="caption">{imageAlt}</Typography>
          </Box>
          <Button
            disabled={field.key === 'Not available' || !!isReadOnly}
            variant="contained"
            color="primary"
            onClick={() => setOpen(true)}
            startIcon={<CloudUploadIcon />}
          >
            {strings.actions.uploadImage}
          </Button>
        </Box>
        <ImageDropzoneDialog
          open={open}
          onClose={() => setOpen(false)}
          maxSize={2 * 1024 * 1024}
          onSave={(files) => {
            onFileSelect(files[0]);
            setOpen(false);
          }}
        />
      </Box>
      <FormHelperText error>{errorMessage}</FormHelperText>
    </>
  );
}

ImageField.propTypes = {
  path: P.string,
  setChange: P.func,
  isReadOnly: P.bool,
  field: P.shape({
    title: P.string.isRequired,
    id: P.string.isRequired,
    type: P.string,
    validation: P.any.isRequired,
    key: P.string.isRequired,
    value: P.string.isRequired,
    isTeamDataValue: P.bool,
  }).isRequired,
};

export default React.memo(ImageField);
